import React from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import Button from "../includes/button";
import { useJobRole } from "./hygraph";
import { SECTION_HEADINGS, equalOpportunity, glanceFacts, keyDetails } from "./jobDetailData";
import { ArrowLeft, CheckCircle, glanceIcons } from "./jobIcons";

const DetailList = ({ items, checks = true }) => (
    <ul className={`flex flex-col p-0 m-0 ${checks ? "gap-4" : "gap-5"}`}>
        {items.map((item) => (
            <li key={item} className="flex items-start gap-3">
                {checks && (
                    <span className="shrink-0 mt-0.5">
                        <CheckCircle aria-hidden="true" focusable="false" />
                    </span>
                )}
                <span className="flex-1 min-w-0 text-base leading-[1.55] text-grey45 whitespace-pre-line">
                    {item}
                </span>
            </li>
        ))}
    </ul>
);

// A section with no items is omitted rather than left as a bare heading, so a
// role that skips "Nice to have" simply does not show it.
const Section = ({ heading, items, checks = true }) =>
    items && items.length ? (
        <section className="w-full flex flex-col gap-5">
            <h2 className="text-2xl lg:text-[28px] font-bold leading-[1.2] text-blue10">{heading}</h2>
            <DetailList items={items} checks={checks} />
        </section>
    ) : null;

const HeroBand = ({ children }) => (
    <div className="relative w-full mt-16 lg:mt-0 p-6 lg:p-24 overflow-hidden">
        <span aria-hidden="true" className="jd-hero-wash pointer-events-none absolute inset-0" />
        <div className="relative container mx-auto flex flex-col gap-8 text-left lg:min-h-[380px] justify-center py-6 lg:py-10">
            {children}
        </div>
    </div>
);

const BackLink = () => (
    <HashLink
        to="/careers#open-roles"
        className="flex items-center gap-2 self-start text-sm font-semibold text-primaryBrand hover:underline"
    >
        <ArrowLeft aria-hidden="true" focusable="false" />
        Back to careers
    </HashLink>
);

const HERO_WASH_CSS = `
    .jd-hero-wash {
        background-image: linear-gradient(128.06deg,
            rgba(238, 244, 249, 0.302) 24.321%,
            rgba(62, 128, 243, 0.149) 69.973%,
            rgba(238, 244, 249, 0) 97.364%);
        -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 58%, transparent 100%);
                mask-image: linear-gradient(to bottom, #000 0%, #000 58%, transparent 100%);
    }
`;

const JobDetail = () => {
    const { roleId } = useParams();
    const { role, loading, error, retry, notFound } = useJobRole(roleId);

    if (loading || error || notFound) {
        return (
            <div className="w-full flex flex-col">
                <Helmet>
                    <title>Careers at Partnered Management Group</title>
                    {/* a role that is missing or could not load must not be indexed */}
                    <meta name="robots" content="noindex" />
                </Helmet>
                <style>{HERO_WASH_CSS}</style>
                <HeroBand>
                    <BackLink />
                    {loading && <p className="text-base text-grey45">Loading this role…</p>}
                    {error && !loading && (
                        <div className="flex flex-col items-start gap-4">
                            <p className="text-base text-grey45">
                                We couldn’t load this role just now. Please try again, or get in touch and we’ll send
                                the details over.
                            </p>
                            <button
                                type="button"
                                onClick={retry}
                                className="text-sm font-semibold text-primaryBrand underline"
                            >
                                Try again
                            </button>
                        </div>
                    )}
                    {notFound && (
                        <div className="flex flex-col items-start gap-4">
                            <h1 className="text-3xl lg:text-5xl font-bold leading-[1.1] text-blue10">
                                That role isn’t open anymore.
                            </h1>
                            <p className="text-base lg:text-xl leading-[1.75] text-grey45">
                                It may have been filled or closed. Have a look at what else we’re hiring for.
                            </p>
                            <Link to="/careers" className="text-sm font-semibold text-primaryBrand underline">
                                See open roles
                            </Link>
                        </div>
                    )}
                </HeroBand>
            </div>
        );
    }

    const facts = glanceFacts(role);
    // Hygraph supplies the destination per role; until one is set, applicants
    // land on the contact form rather than a dead link.
    const applyHref = role.applyUrl || "/careers#contact";
    const applyIsExternal = /^https?:\/\//i.test(applyHref);

    const ApplyButton = ({ className }) => (
        <Button
            to={applyHref}
            label="Apply Now"
            variant="light-primary"
            isHashLink={!applyIsExternal}
            target={applyIsExternal ? "_blank" : undefined}
            className={className}
        />
    );

    const pageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `https://www.partneredmg.com/careers/${role.slug}#webpage`,
        "url": `https://www.partneredmg.com/careers/${role.slug}`,
        "name": `${role.title} | Careers at Partnered Management Group`,
        "description": role.summary,
        "isPartOf": { "@id": "https://www.partneredmg.com/#website" },
    };

    return (
        <div className="w-full flex flex-col">
            <Helmet>
                <title>{`${role.title} | Careers at Partnered Management Group`}</title>
                <meta name="description" content={role.summary} />
                <link rel="canonical" href={`https://www.partneredmg.com/careers/${role.slug}`} />
                <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
            </Helmet>

            <style>{`
                ${HERO_WASH_CSS}

                /* Decorative lift only: the card is not a link and gains no
                   cursor or tab stop. */
                .jd-glance { transition: transform .18s ease-out, box-shadow .18s ease-out; }
                @media (hover: hover) and (pointer: fine) {
                    .jd-glance:hover {
                        transform: translateY(-4px);
                        box-shadow: 0 18px 38px -20px rgba(15,20,36,0.26);
                        transition-duration: .2s;
                    }
                }
                @media (prefers-reduced-motion: reduce) {
                    .jd-glance, .jd-glance:hover { transform: none; transition: none; }
                }
            `}</style>

            <HeroBand>
                <BackLink />
                <div className="flex flex-col gap-4 lg:gap-[18px]">
                    <p className="text-xs font-bold tracking-[2.16px] text-primaryBrand">
                        {role.teamLabel.toUpperCase()}
                    </p>
                    <h1 className="text-3xl md:text-5xl lg:text-[56px] font-bold leading-[1.08] tracking-[-0.56px] text-blue10">
                        {role.title}
                    </h1>
                    <p className="max-w-[700px] text-base lg:text-xl leading-[1.75] text-grey45 whitespace-pre-line">{role.summary}</p>
                </div>
            </HeroBand>

            <div className="w-full">
                <div className="container mx-auto px-6 md:px-12 lg:px-20 pt-8 lg:pt-14 pb-20 lg:pb-32 flex flex-col lg:flex-row items-start gap-12 lg:gap-[72px] text-left">
                    <div className="flex-1 min-w-0 w-full flex flex-col gap-12 lg:gap-14">
                        <section className="w-full flex flex-col gap-5">
                            <h2 className="text-2xl lg:text-4xl font-bold leading-[1.12] text-blue10">
                                {role.overviewHeading}
                            </h2>
                            {role.overviewBody.map((paragraph) => (
                                <p key={paragraph} className="text-base leading-[1.5] text-grey45 whitespace-pre-line">
                                    {paragraph}
                                </p>
                            ))}
                        </section>

                        <Section heading={SECTION_HEADINGS.responsibilities} items={role.responsibilities} />
                        <Section heading={SECTION_HEADINGS.qualifications} items={role.qualifications} />
                        <Section heading={SECTION_HEADINGS.niceToHave} items={role.niceToHave} />
                        <Section heading={SECTION_HEADINGS.keyDetails} items={keyDetails(role)} checks={false} />

                        {role.whyJoinUs.length > 0 && (
                            <section className="w-full flex flex-col gap-5">
                                <h2 className="text-2xl lg:text-[28px] font-bold leading-[1.2] text-blue10">
                                    {SECTION_HEADINGS.whyJoinUs}
                                </h2>
                                <DetailList items={role.whyJoinUs} checks={false} />
                                <div className="mt-6 lg:mt-8">
                                    <ApplyButton className="md:!w-[280px] !h-14" />
                                </div>
                            </section>
                        )}

                        <section className="w-full border-t border-solid border-borderPrimary pt-7 flex flex-col gap-2.5">
                            <h2 className="text-base font-bold text-blue10">{equalOpportunity.heading}</h2>
                            <p className="text-sm leading-[1.6] text-[#6B6F7A]">{equalOpportunity.body}</p>
                        </section>
                    </div>

                    <aside className="hidden lg:block w-full lg:w-[380px] lg:shrink-0">
                        <div className="jd-glance bg-white border-[1.5px] border-solid border-FGNeutralAccent rounded-3xl p-8 flex flex-col gap-7">
                            <div className="flex flex-col gap-2">
                                <p className="text-xs font-bold tracking-[2.16px] text-primaryBrand">AT A GLANCE</p>
                                <p className="text-2xl font-bold leading-[1.2] text-blue10">Your role at PMG</p>
                            </div>

                            <ul className="flex flex-col gap-[22px] p-0 m-0">
                                {facts.map((fact) => {
                                    const Icon = glanceIcons[fact.icon];
                                    return (
                                        <li key={fact.id} className="flex items-center gap-3.5">
                                            <span className="w-10 h-10 shrink-0 rounded-xl bg-[#EEF4F9] flex items-center justify-center">
                                                <Icon aria-hidden="true" focusable="false" />
                                            </span>
                                            <span className="flex-1 min-w-0 flex flex-col gap-1">
                                                <span className="text-xs font-bold tracking-[0.96px] text-[#6B6F7A]">
                                                    {fact.label}
                                                </span>
                                                <span className="text-base font-medium leading-[1.4] text-blue10">
                                                    {fact.value}
                                                </span>
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>

                            <span className="block pt-2">
                                <ApplyButton className="!w-full !h-[52px]" />
                            </span>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default JobDetail;
